import { Dropdown } from "antd";
import PropTypes from "prop-types";
import { useMemo, useRef, useState } from "react";
import Input from "shared/components/Input";

const TRIGGER_CHARS = ["{", "["];

const findTriggerPosition = (text, cursor) => {
  for (let position = cursor - 1; position >= 0; position -= 1) {
    const char = text[position];
    if (char === "]" || char === "}") return null;
    if (TRIGGER_CHARS.includes(char)) return position;
  }
  return null;
};

const FileNameTemplateInput = ({ value, onChange, fields, ...inputProps }) => {
  const [trigger, setTrigger] = useState(null);
  const innerRef = useRef(null);
  const blurTimerRef = useRef(null);

  const syncTrigger = (text, cursor) => {
    const position = findTriggerPosition(text || "", cursor || 0);
    setTrigger(
      position === null
        ? null
        : { position, search: (text || "").slice(position + 1, cursor) },
    );
  };

  const items = useMemo(() => {
    if (!trigger) return [];
    const search = trigger.search.toLowerCase();
    return fields
      .filter(({ label, value: field }) =>
        `${label} ${field}`.toLowerCase().includes(search),
      )
      .map(({ label, value: field }) => ({ key: field, label }));
  }, [fields, trigger]);

  const insertField = (field) => {
    if (!trigger) return;
    clearTimeout(blurTimerRef.current);
    const text = value || "";
    // End of the typed search fragment, right after the trigger char.
    const fragmentEnd = trigger.position + 1 + trigger.search.length;
    const inserted = `[${field}]`;
    onChange(
      `${text.slice(0, trigger.position)}${inserted}${text.slice(fragmentEnd)}`,
    );
    setTrigger(null);
    const caret = trigger.position + inserted.length;
    requestAnimationFrame(() => {
      const input = innerRef.current?.input;
      if (input) {
        input.focus();
        input.setSelectionRange(caret, caret);
      }
    });
  };

  return (
    <Dropdown
      open={Boolean(trigger) && items.length > 0}
      trigger={[]}
      menu={{
        items,
        onClick: ({ key }) => insertField(key),
        style: { maxHeight: 320, overflowY: "auto" },
      }}
    >
      <div>
        <Input
          {...inputProps}
          innerRef={innerRef}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            syncTrigger(e.target.value, e.target.selectionStart);
          }}
          onClick={(e) => syncTrigger(e.target.value, e.target.selectionStart)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setTrigger(null);
          }}
          onBlur={() => {
            blurTimerRef.current = setTimeout(() => setTrigger(null), 200);
          }}
        />
      </div>
    </Dropdown>
  );
};

FileNameTemplateInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ).isRequired,
};

export default FileNameTemplateInput;
